import { useRef, useCallback, useMemo } from "react";
import type { POI, MapCanvasRef } from "../../components/MapCanvas";
import { useSheetStack, selectCurrent, selectPrevious, type SheetEntry } from "../../stores/useSheetStack";
import { analytics } from "../../lib/analytics";

/**
 * Bottom padding for flyTo so the POI marker lands just above the sheet.
 * We use ~55% of the snap height (not 100%) so the marker sits in the lower
 * portion of the visible map — close to the sheet, not centered in remaining space.
 */
const HALF_SNAP_PADDING = 0.30; // positions marker ~35% down from top
const PEEK_PADDING = 100;       // modest offset for 160px peek

function sheetPadding(context: "map" | "peek") {
  const bottom = context === "map"
    ? window.innerHeight * HALF_SNAP_PADDING
    : PEEK_PADDING;
  return { top: 0, bottom, left: 0, right: 0 };
}

export function useVisitorPOISelection(
  mapRef: React.RefObject<MapCanvasRef | null>,
  closeListView: () => void,
  pois: POI[],
) {
  const { push, pop, replace, clear } = useSheetStack();
  const poisViewedRef = useRef(0);

  // Derive current/previous from stack via Zustand selectors
  const current = useSheetStack(selectCurrent);
  const previous = useSheetStack(selectPrevious);

  // Derive selectedPOI from stack
  const poiEntry = useMemo(() => {
    if (current?.type === "poi") return current;
    return null;
  }, [current]);

  const selectedPOI = useMemo(() => {
    if (!poiEntry) return null;
    return pois.find((p) => p.id === poiEntry.poiId) ?? null;
  }, [poiEntry, pois]);

  const sheetContext = poiEntry?.context ?? null;

  // Derived boolean states
  const isPOISheetOpen = poiEntry !== null;
  const isEventsSheetOpen = current?.type === "events";
  const isCuratedListBrowserOpen = current?.type === "curated";
  const isSponsorsSheetOpen = current?.type === "sponsors";
  const isFaqSheetOpen = current?.type === "faq";

  const handleMapPOIClick = useCallback((poi: POI) => {
    poisViewedRef.current += 1;
    analytics.selectPoi({
      poi_id: poi.id,
      poi_name: poi.name,
      category: poi.categorySlug ?? undefined,
      method: "map_click",
      is_sponsored: !!poi.sponsorTier,
      sponsor_tier: poi.sponsorTier,
    });
    replace({ type: "poi", poiId: poi.id, context: "map" });
    if (poi.location) {
      mapRef.current?.flyTo([poi.location.coordinates[0], poi.location.coordinates[1]], undefined, sheetPadding("map"));
    }
  }, [mapRef, replace]);

  const handleListPOIClick = useCallback((poi: POI) => {
    poisViewedRef.current += 1;
    analytics.selectPoi({
      poi_id: poi.id,
      poi_name: poi.name,
      category: poi.categorySlug ?? undefined,
      method: "list_click",
      is_sponsored: !!poi.sponsorTier,
      sponsor_tier: poi.sponsorTier,
    });
    replace({ type: "poi", poiId: poi.id, context: "list" });
  }, [replace]);

  const handleFeaturedPOIClick = useCallback((poi: POI) => {
    analytics.selectPoi({
      poi_id: poi.id,
      poi_name: poi.name,
      category: poi.categorySlug ?? undefined,
      method: "featured_click",
      is_sponsored: true,
      sponsor_tier: poi.sponsorTier,
    });
    replace({ type: "poi", poiId: poi.id, context: "list" });
  }, [replace]);

  /** Close all sheets (clear stack) */
  const handleSheetClose = useCallback(() => {
    clear();
  }, [clear]);

  const handleViewOnMap = useCallback(() => {
    if (!selectedPOI?.location) return;
    const coords: [number, number] = [
      selectedPOI.location.coordinates[0],
      selectedPOI.location.coordinates[1],
    ];
    closeListView();
    mapRef.current?.flyTo(coords, undefined, sheetPadding("map"));
    // Switch context to map
    setTimeout(() => {
      replace({ type: "poi", poiId: selectedPOI.id, context: "map" });
    }, 100);
  }, [selectedPOI, closeListView, mapRef, replace]);

  /** Open events sheet */
  const openEventsSheet = useCallback(() => {
    replace({ type: "events" });
  }, [replace]);

  /** Close events sheet */
  const closeEventsSheet = useCallback(() => {
    if (current?.type === "events") clear();
  }, [current, clear]);

  /** Open curated list browser */
  const openCuratedListBrowser = useCallback(() => {
    replace({ type: "curated" });
  }, [replace]);

  /** Close curated list browser */
  const closeCuratedListBrowser = useCallback(() => {
    if (current?.type === "curated") clear();
  }, [current, clear]);

  /** Open sponsors sheet */
  const openSponsorsSheet = useCallback(() => {
    replace({ type: "sponsors" });
  }, [replace]);

  /** Close sponsors sheet */
  const closeSponsorsSheet = useCallback(() => {
    if (current?.type === "sponsors") clear();
  }, [current, clear]);

  /** Open FAQ sheet */
  const openFaqSheet = useCallback(() => {
    replace({ type: "faq" });
  }, [replace]);

  /** Close FAQ sheet */
  const closeFaqSheet = useCallback(() => {
    if (current?.type === "faq") clear();
  }, [current, clear]);

  /** Push POI from events/curated context (navigates within stack) */
  const pushPOIFromSheet = useCallback((poi: POI, context: SheetEntry & { type: "poi" }) => {
    poisViewedRef.current += 1;
    analytics.selectPoi({
      poi_id: poi.id,
      poi_name: poi.name,
      category: poi.categorySlug ?? undefined,
      method: "list_click",
      is_sponsored: !!poi.sponsorTier,
      sponsor_tier: poi.sponsorTier,
    });
    push(context);
    if (poi.location) {
      mapRef.current?.flyTo([poi.location.coordinates[0], poi.location.coordinates[1]], undefined, sheetPadding("map"));
    }
  }, [push, mapRef]);

  /** Back button — pop to previous sheet */
  const handleBack = useCallback(() => {
    pop();
  }, [pop]);

  /** Re-open POI detail sheet (e.g. after cancelling route preview) */
  const reopenPOI = useCallback((poiId: string) => {
    replace({ type: "poi", poiId, context: "map" });
  }, [replace]);

  return {
    selectedPOI,
    isPOISheetOpen,
    sheetContext,
    poisViewedRef,
    handleMapPOIClick,
    handleListPOIClick,
    handleFeaturedPOIClick,
    handleSheetClose,
    handleViewOnMap,
    // Stack-based state
    current,
    previous,
    isEventsSheetOpen,
    isCuratedListBrowserOpen,
    isSponsorsSheetOpen,
    isFaqSheetOpen,
    openEventsSheet,
    closeEventsSheet,
    openCuratedListBrowser,
    closeCuratedListBrowser,
    openSponsorsSheet,
    closeSponsorsSheet,
    openFaqSheet,
    closeFaqSheet,
    pushPOIFromSheet,
    handleBack,
    reopenPOI,
    // Direct stack access for deep links
    replace,
    clear,
  };
}
